/*
 Выравниваем высоту параметров внутри одной строки, когда их в строке несколько
 в разных колонках.

 Как это устроено:
   1. Раньше (в useTableModel) мы уже сделали так, что во всех таких ячейках одной
      строки одинаковое число параметров и идут они в одном порядке. То есть по
      вертикали они и так стоят друг напротив друга, без всяких замеров.
   2. А вот высота одного и того же параметра в разных колонках может отличаться,
      если где-то текст длиннее и перенёсся на две строки. Вот их высоту мы тут и
      подгоняем друг под друга, проставляя на строку нужные размеры, а каждый
      параметр в разметке держит эту минимальную высоту.

 Чего придерживаемся:
   - следим за изменением размеров, а не дёргаем по таймеру.
   - если за один раз поменялось сразу несколько параметров, пересчитываем один раз.
   - перед замером сбрасываем заданные высоты, чтобы взять настоящую высоту
     содержимого, а не ту, что мы сами проставили в прошлый раз. Иначе при
     уменьшении текста в одной колонке остальные так и останутся раздутыми.
   - ничего не шлём между ячейками, всё считается внутри своей строки.
   - всё это только для строк, где параметров правда несколько.

 Когда строку уводят за пределы экрана и её убирают, слежение само отключается,
 ничего висеть не остаётся.
*/

import { onBeforeUnmount, onMounted, watch, type Ref } from "vue";
import { isColumnResizing, isWindowResizing } from "./resizeState";
import { requestParamHeightAlign, cancelParamHeightAlign, type ParamHeightJob } from "./paramHeightScheduler";

const SUB_ROW_SELECTOR = "[data-qtl-param-idx]";
const VAR_PREFIX = "--qtl-psub-";

export interface UseRowParameterHeightsArgs {
  /** ссылка на саму строку */
  rowEl: Ref<HTMLElement | null>;
  /**
   * включить выравнивание для этой строки. Если поставить false, слежение
   * отключается. Это нужно, чтобы при подмене данных в строке правильно
   * включать и выключать выравнивание, не пересоздавая саму строку.
   * Если не задавать, выравнивание работает всегда.
   */
  enabled?: Ref<boolean>;
  /**
   * по смене этого значения пересчитываем заново. Сюда обычно кладут отпечаток
   * колонок и ширин: когда ширины меняются, строку перерисует и слежение само
   * поймает изменения, но иногда полезно пнуть руками, например при первой загрузке.
   */
  trigger?: Ref<unknown>;
}

export function useRowParameterHeights(args: UseRowParameterHeightsArgs) {
  let observer: ResizeObserver | null = null;
  let mutationObserver: MutationObserver | null = null;
  /*
   measuring включается, пока мы сами меняем высоты. Слежение за размером на это
   тоже сработает, и такие срабатывания надо пропустить, иначе получится
   бесконечный круг. Снимаем признак через пару кадров, когда всё уже отрисовалось.
  */
  let measuring = false;
  let attached = false;
  // промежуточные данные между шагами одного пересчёта: что замерили в measure,
  // то проставим в apply
  let pendingMax: Map<number, number> | null = null;

  function clearVars(el: HTMLElement, indices: Iterable<number>) {
    for (const idx of indices) {
      el.style.setProperty(`${VAR_PREFIX}${idx}-h`, "0px");
    }
  }

  function applyVars(el: HTMLElement, max: Map<number, number>) {
    for (const [idx, h] of max) {
      el.style.setProperty(`${VAR_PREFIX}${idx}-h`, `${Math.ceil(h)}px`);
    }
  }

  /*
   Пересчёт высот разбит на три шага, чтобы общий планировщик мог сделать его для
   всех строк разом: сбросить высоты, прочитать настоящие размеры и проставить итог.
  */
  const job: ParamHeightJob = {
    clear() {
      pendingMax = null;
      const el = args.rowEl.value;
      if (!el) return;
      const subRows = el.querySelectorAll<HTMLElement>(SUB_ROW_SELECTOR);
      if (!subRows.length) return;
      const indices = new Set<number>();
      for (const sr of subRows) {
        const v = Number(sr.dataset.qtlParamIdx);
        if (Number.isFinite(v)) indices.add(v);
      }
      if (!indices.size) return;
      measuring = true;
      clearVars(el, indices);
    },
    measure() {
      const el = args.rowEl.value;
      if (!el || !measuring) return;
      const subRows = el.querySelectorAll<HTMLElement>(SUB_ROW_SELECTOR);
      const max = new Map<number, number>();
      for (const sr of subRows) {
        const idx = Number(sr.dataset.qtlParamIdx);
        if (!Number.isFinite(idx)) continue;
        // берём настоящую высоту элемента на экране, с учётом масштаба и дробных пикселей
        const h = sr.getBoundingClientRect().height;
        const cur = max.get(idx) ?? 0;
        if (h > cur) max.set(idx, h);
      }
      pendingMax = max;
    },
    apply() {
      const el = args.rowEl.value;
      if (el && pendingMax) applyVars(el, pendingMax);
      pendingMax = null;
      // снимаем признак через пару кадров, когда слежение уже отреагирует на наши же
      // изменения. Иначе получится бесконечный круг.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          measuring = false;
        });
      });
    },
  };

  function schedule() {
    if (measuring) return;
    // Пока идёт ресайз — тянут границу колонки или меняют размер окна — не
    // пересчитываем высоты на каждом кадре, а то при большом числе строк всё
    // тормозит. Тут просто выходим, а один общий пересчёт случится уже после того,
    // как ресайз закончился (см. resizeState и rowHeightAlignStamp в QueryTableLite).
    if (isColumnResizing.value || isWindowResizing.value) return;
    requestParamHeightAlign(job);
  }

  function attach() {
    if (attached) return;
    const el = args.rowEl.value;
    if (!el) return;

    attached = true;

    // одно общее слежение за размером всех параметров строки. Меняется любой, планируем пересчёт
    observer = new ResizeObserver(() => {
      if (measuring) return;
      schedule();
    });

    const subRows = el.querySelectorAll<HTMLElement>(SUB_ROW_SELECTOR);
    for (const sr of subRows) {
      observer.observe(sr);
    }

    // ещё следим за тем, что состав параметров в строке поменялся (например, в строку
    // подставили другие данные). Тогда заново навешиваем слежение на новый набор.
    mutationObserver = new MutationObserver(() => {
      if (!observer) return;
      observer.disconnect();
      const fresh = el.querySelectorAll<HTMLElement>(SUB_ROW_SELECTOR);
      for (const sr of fresh) observer.observe(sr);
      schedule();
    });
    mutationObserver.observe(el, { childList: true, subtree: true });

    // первый пересчёт
    schedule();
  }

  function detach() {
    attached = false;
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    if (mutationObserver) {
      mutationObserver.disconnect();
      mutationObserver = null;
    }
    cancelParamHeightAlign(job);
    pendingMax = null;
    measuring = false;
  }

  function isEnabled(): boolean {
    return args.enabled ? !!args.enabled.value : true;
  }

  onMounted(() => {
    if (isEnabled()) attach();
  });

  onBeforeUnmount(() => {
    detach();
  });

  if (args.enabled) {
    // включаем и выключаем выравнивание, когда меняется enabled (например, при подмене данных в строке)
    watch(
      args.enabled,
      (v) => {
        if (v) attach();
        else detach();
      },
    );
  }

  if (args.trigger) {
    // пересчёт по внешнему сигналу, например когда поменялись ширины колонок
    watch(args.trigger, () => { if (isEnabled()) schedule(); }, { flush: "post" });
  }

  return { schedule, attach, detach };
}
